/**
 * PriceSets — 3-panel workspace for creating and managing contractor price sets.
 *
 * Layout:
 *   [Left: Set Library] | [Center: Set Workspace] | [Right: Market Intel]
 */
import {
    Box,
    Grid,
    GridItem,
    Heading,
    Button,
    Flex,
    Text,
    Icon,
    HStack,
    VStack,
    Input,
    Textarea,
    Select,
    Spinner,
    Center,
    IconButton,
    Badge,
    Tooltip,
    Divider,
    useDisclosure,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    useColorModeValue,
} from '@chakra-ui/react';

import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    MdAdd,
    MdDownload,
    MdTrendingUp,
    MdAttachMoney,
    MdEdit,
    MdCheck,
    MdClose,
    MdTableRows,
} from 'react-icons/md';

import { PriceSetSidebar } from '@/components/PriceSetSidebar';
import { PriceSetLineItemRow } from '@/components/PriceSetLineItemRow';
import { MarketIntelPanel } from '@/components/MarketIntelPanel';
import {
    usePriceSets,
    usePriceSet,
    useCreatePriceSet,
    useUpdatePriceSet,
    useAddLineItem,
    useReorderLineItems,
    useExportPriceSet,
} from '@/services/hooks';

const CATEGORY_OPTIONS = ['Materials', 'Labor', 'Equipment', 'Subcontractor', 'Other'];

const fmt = (n: number | null) =>
    n == null ? '—' : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,
    }).format(n);

// ─── New Set Modal ───────────────────────────────────────────────────────────

function NewSetModal({ isOpen, onClose, onCreated }: {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (id: number) => void;
}) {
    const toast = useToast();
    const createMutation = useCreatePriceSet();
    const [form, setForm] = useState({ name: '', description: '', category: '' });

    const handleCreate = async () => {
        if (!form.name.trim()) return;
        try {
            const newSet = await createMutation.mutateAsync({
                name: form.name.trim(),
                description: form.description || undefined,
                category: form.category || undefined,
            });
            toast({ title: `"${newSet.name}" created`, status: 'success', duration: 2000 });
            onCreated(newSet.id);
            onClose();
            setForm({ name: '', description: '', category: '' });
        } catch {
            toast({ title: 'Failed to create price set', status: 'error', duration: 3000 });
        }
    };

    const modalBg = useColorModeValue('white', 'navy.800');
    const modalBorder = useColorModeValue('gray.200', 'navy.600');

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent bg={modalBg} borderColor={modalBorder} borderWidth="1px">
                <ModalHeader>New Price Set</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4}>
                        <FormControl isRequired>
                            <FormLabel fontSize="sm">Name</FormLabel>
                            <Input
                                placeholder='e.g. "Concrete Sidewalk Package"'
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                            />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm">Category</FormLabel>
                            <Select
                                placeholder="Select category..."
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                            >
                                {CATEGORY_OPTIONS.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="sm">Description</FormLabel>
                            <Textarea
                                placeholder="Optional — what's this set used for?"
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                rows={3}
                                resize="none"
                            />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button
                        colorScheme="brand"
                        onClick={handleCreate}
                        isLoading={createMutation.isPending}
                        isDisabled={!form.name.trim()}
                    >
                        Create Set
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

// ─── Add Line Item Row ────────────────────────────────────────────────────────

function AddLineItemRow({ setId }: { setId: number }) {
    const toast = useToast();
    const addMutation = useAddLineItem();
    const [active, setActive] = useState(false);
    const [form, setForm] = useState({ name: '', category: '', uom: 'EA', unit_price: '' });

    const rowBg = useColorModeValue('white', 'navy.700');
    const inputColor = useColorModeValue('gray.800', 'gray.100');
    const placeholderColor = useColorModeValue('gray.400', 'gray.500');
    const selectColor = useColorModeValue('gray.800', 'gray.300');
    const selectPlaceholderColor = useColorModeValue('gray.400', 'gray.600');
    const currencyColor = useColorModeValue('gray.500', 'gray.500');
    const priceColor = useColorModeValue('green.600', 'green.300');
    const helpTextColor = useColorModeValue('gray.500', 'gray.600');
    const btnHoverBg = useColorModeValue('gray.100', 'navy.700');
    const rowBoxShadow = useColorModeValue('sm', 'none');

    const handleAdd = async () => {
        const price = parseFloat(form.unit_price);
        if (!form.name.trim() || isNaN(price)) {
            toast({ title: 'Name and unit price are required', status: 'warning', duration: 2000 });
            return;
        }
        try {
            await addMutation.mutateAsync({
                setId,
                data: {
                    name: form.name.trim(),
                    category: form.category || null,
                    uom: form.uom,
                    unit_price: price,
                },
            });
            setForm({ name: '', category: '', uom: 'EA', unit_price: '' });
            // Keep form open for rapid entry
        } catch {
            toast({ title: 'Failed to add item', status: 'error', duration: 2000 });
        }
    };

    if (!active) {
        return (
            <Button
                leftIcon={<MdAdd />}
                size="sm"
                variant="ghost"
                colorScheme="brand"
                onClick={() => setActive(true)}
                w="full"
                justifyContent="flex-start"
                mt={1}
                color="brand.400"
                _hover={{ bg: btnHoverBg }}
            >
                Add line item
            </Button>
        );
    }

    return (
        <Box px={2} py={2} borderRadius="md" bg={rowBg} borderLeft="2px solid" borderLeftColor="brand.400" mt={1} boxShadow={rowBoxShadow}>
            <HStack spacing={2} align="center">
                <Input
                    size="sm"
                    placeholder="Item name *"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    variant="unstyled"
                    fontWeight="500"
                    flex={3}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setActive(false); }}
                    minW="120px"
                    color={inputColor}
                    _placeholder={{ color: placeholderColor }}
                />
                <Select
                    size="sm"
                    variant="unstyled"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    color={form.category ? selectColor : selectPlaceholderColor}
                    flex={1.5}
                    minW="90px"
                    fontSize="xs"
                >
                    <option value="">Category</option>
                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
                <Select
                    size="sm"
                    variant="unstyled"
                    value={form.uom}
                    onChange={(e) => setForm({ ...form, uom: e.target.value })}
                    color={selectColor}
                    flex={1}
                    minW="60px"
                    fontSize="xs"
                >
                    {['EA', 'LS', 'LF', 'SF', 'SY', 'CY', 'TN', 'HR', 'DAY', 'MO', 'GAL', 'LB'].map((u) => (
                        <option key={u} value={u}>{u}</option>
                    ))}
                </Select>
                <Box flex={1.5} position="relative" minW="90px">
                    <Text position="absolute" left={1.5} top="50%" transform="translateY(-50%)" fontSize="xs" color={currencyColor} zIndex={1}>$</Text>
                    <Input
                        size="sm"
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="0.00"
                        value={form.unit_price}
                        onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                        variant="unstyled"
                        fontWeight="600"
                        color={priceColor}
                        pl={4}
                        textAlign="right"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                        _placeholder={{ color: placeholderColor }}
                    />
                </Box>
                <HStack spacing={1} flexShrink={0}>
                    <Tooltip label="Add item (Enter)" hasArrow>
                        <IconButton
                            aria-label="Add"
                            icon={<MdCheck />}
                            size="xs"
                            colorScheme="brand"
                            onClick={handleAdd}
                            isLoading={addMutation.isPending}
                        />
                    </Tooltip>
                    <Tooltip label="Cancel (Esc)" hasArrow>
                        <IconButton
                            aria-label="Cancel"
                            icon={<MdClose />}
                            size="xs"
                            variant="ghost"
                            colorScheme="gray"
                            onClick={() => setActive(false)}
                        />
                    </Tooltip>
                </HStack>
            </HStack>
            <Text fontSize="9px" color={helpTextColor} mt={1.5} pl={1}>Press Enter to add • Esc to cancel</Text>
        </Box>
    );
}

// ─── Set Workspace (center panel) ────────────────────────────────────────────

function SetWorkspace({ setId }: { setId: number }) {
    const toast = useToast();
    const { data: set, isLoading } = usePriceSet(setId);
    const updateMutation = useUpdatePriceSet();
    const reorderMutation = useReorderLineItems();
    const exportMutation = useExportPriceSet();

    const [editingName, setEditingName] = useState(false);
    const [nameVal, setNameVal] = useState('');
    const [localItems, setLocalItems] = useState<any[]>([]);

    useEffect(() => {
        if (set) {
            setLocalItems(set.line_items || []);
            setNameVal(set.name);
        }
    }, [set]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const oldIndex = localItems.findIndex((i) => i.id === active.id);
            const newIndex = localItems.findIndex((i) => i.id === over.id);
            const reordered = arrayMove(localItems, oldIndex, newIndex);
            setLocalItems(reordered);
            try {
                await reorderMutation.mutateAsync({ setId, orderedIds: reordered.map((i) => i.id) });
            } catch {
                toast({ title: 'Reorder failed', status: 'error', duration: 2000 });
                setLocalItems(localItems); // revert
            }
        },
        [localItems, setId, reorderMutation, toast]
    );

    const handleSaveName = async () => {
        if (!nameVal.trim() || nameVal === set?.name) { setEditingName(false); return; }
        try {
            await updateMutation.mutateAsync({ id: setId, data: { name: nameVal.trim() } });
            toast({ title: 'Renamed', status: 'success', duration: 1500 });
        } catch {
            toast({ title: 'Failed to rename', status: 'error', duration: 2000 });
        }
        setEditingName(false);
    };

    const handleExport = async (format: 'csv' | 'xlsx') => {
        try {
            const blob = await exportMutation.mutateAsync({ setId, format });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${set?.name || 'price-set'}.${format}`;
            a.click();
            URL.revokeObjectURL(url);
            toast({ title: `Exported as ${format.toUpperCase()}`, status: 'success', duration: 2000 });
        } catch {
            toast({ title: 'Export failed', status: 'error', duration: 2000 });
        }
    };

    const titleColor = useColorModeValue('gray.800', 'gray.100');
    const descColor = useColorModeValue('gray.600', 'gray.500');
    const statLabelColor = useColorModeValue('gray.500', 'gray.500');
    const statValueColor = useColorModeValue('gray.800', 'gray.100');
    const dividerColor = useColorModeValue('gray.200', 'navy.700');
    const tableHeaderColor = useColorModeValue('gray.500', 'gray.500');
    const emptyIconColor = useColorModeValue('gray.400', 'gray.700');
    const emptyTextColor = useColorModeValue('gray.500', 'gray.500');
    const emptySubtextColor = useColorModeValue('gray.400', 'gray.600');

    if (isLoading) {
        return (
            <Center h="full">
                <Spinner size="lg" color="brand.500" />
            </Center>
        );
    }

    if (!set) return null;

    return (
        <Box h="full" display="flex" flexDir="column" px={6} py={5} overflowY="auto">
            {/* Set header */}
            <Flex justify="space-between" align="flex-start" mb={2} flexWrap="wrap" gap={3}>
                <Box>
                    {editingName ? (
                        <HStack>
                            <Input
                                value={nameVal}
                                onChange={(e) => setNameVal(e.target.value)}
                                size="md"
                                variant="flushed"
                                fontWeight="700"
                                fontSize="xl"
                                autoFocus
                                onBlur={handleSaveName}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                                borderColor="brand.400"
                                color={titleColor}
                                w="300px"
                            />
                            <IconButton aria-label="Save name" icon={<MdCheck />} size="sm" colorScheme="brand" onClick={handleSaveName} />
                        </HStack>
                    ) : (
                        <HStack>
                            <Heading size="md" color={titleColor}>{set.name}</Heading>
                            <Tooltip label="Rename" hasArrow>
                                <IconButton
                                    aria-label="Rename"
                                    icon={<MdEdit />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="gray"
                                    onClick={() => setEditingName(true)}
                                />
                            </Tooltip>
                            {set.is_shared && (
                                <Badge colorScheme="blue" variant="subtle" fontSize="xs">Shared</Badge>
                            )}
                            {set.category && (
                                <Badge colorScheme="purple" variant="subtle" fontSize="xs">{set.category}</Badge>
                            )}
                        </HStack>
                    )}
                    {set.description && (
                        <Text fontSize="sm" color={descColor} mt={1}>{set.description}</Text>
                    )}
                </Box>

                {/* Actions */}
                <HStack spacing={2} flexShrink={0}>
                    <Button
                        leftIcon={<MdDownload />}
                        size="sm"
                        variant="outline"
                        colorScheme="gray"
                        onClick={() => handleExport('csv')}
                        isLoading={exportMutation.isPending}
                        fontSize="xs"
                    >
                        Export CSV
                    </Button>
                </HStack>
            </Flex>

            {/* Stats bar */}
            <HStack spacing={6} mb={5} px={1}>
                <Box>
                    <Text fontSize="xs" color={statLabelColor}>Items</Text>
                    <Text fontWeight="700" color={statValueColor}>{set.items_count}</Text>
                </Box>
                {set.avg_unit_price != null && (
                    <Box>
                        <Text fontSize="xs" color={statLabelColor}>Avg Price</Text>
                        <Text fontWeight="700" color={statValueColor}>{fmt(set.avg_unit_price)}</Text>
                    </Box>
                )}
                {set.min_unit_price != null && (
                    <Box>
                        <Text fontSize="xs" color={statLabelColor}>Range</Text>
                        <Text fontWeight="700" color={statValueColor}>{fmt(set.min_unit_price)} – {fmt(set.max_unit_price)}</Text>
                    </Box>
                )}
            </HStack>

            <Divider borderColor={dividerColor} mb={4} />

            {/* Column headers */}
            {localItems.length > 0 && (
                <HStack px={3} mb={1} spacing={2}>
                    <Box w={4} />
                    <Text flex={3} fontSize="xs" color={tableHeaderColor} textTransform="uppercase" letterSpacing="wider" minW="120px">Name</Text>
                    <Text flex={1.5} fontSize="xs" color={tableHeaderColor} textTransform="uppercase" letterSpacing="wider" minW="90px">Category</Text>
                    <Text flex={1} fontSize="xs" color={tableHeaderColor} textTransform="uppercase" letterSpacing="wider" minW="60px">UOM</Text>
                    <Text flex={1.5} fontSize="xs" color={tableHeaderColor} textTransform="uppercase" letterSpacing="wider" textAlign="right" minW="90px">Unit Price</Text>
                    <Box w={6} />
                </HStack>
            )}

            {/* DnD sortable list */}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis]}
            >
                <SortableContext items={localItems.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                    <VStack spacing={1} align="stretch">
                        {localItems.map((item) => (
                            <PriceSetLineItemRow key={item.id} item={item} setId={setId} />
                        ))}
                    </VStack>
                </SortableContext>
            </DndContext>

            {/* Add item */}
            <AddLineItemRow setId={setId} />

            {/* Empty state */}
            {localItems.length === 0 && (
                <Center py={12} flexDir="column">
                    <Icon as={MdTableRows} boxSize={10} color={emptyIconColor} mb={3} />
                    <Text color={emptyTextColor} fontSize="sm" mb={1}>No line items yet</Text>
                    <Text color={emptySubtextColor} fontSize="xs">Click "Add line item" above to get started</Text>
                </Center>
            )}
        </Box>
    );
}

// ─── Empty workspace (no set selected) ───────────────────────────────────────

function EmptyWorkspace({ onNew }: { onNew: () => void }) {
    const boxBg = useColorModeValue('white', 'navy.800');
    const boxBorder = useColorModeValue('gray.200', 'navy.700');
    const iconBg = useColorModeValue('brand.50', 'brand.900');
    const titleColor = useColorModeValue('gray.800', 'gray.100');
    const textColor = useColorModeValue('gray.600', 'gray.500');

    return (
        <Center h="full" flexDir="column" gap={4}>
            <Box
                p={6}
                borderRadius="2xl"
                bg={boxBg}
                borderWidth="1px"
                borderColor={boxBorder}
                textAlign="center"
                maxW="320px"
                boxShadow={useColorModeValue('sm', 'none')}
            >
                <Box
                    w={14}
                    h={14}
                    borderRadius="xl"
                    bg={iconBg}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mx="auto"
                    mb={4}
                >
                    <Icon as={MdAttachMoney} boxSize={7} color="brand.500" />
                </Box>
                <Heading size="sm" mb={2} color={titleColor}>Your Estimating Library</Heading>
                <Text fontSize="sm" color={textColor} mb={5}>
                    Create reusable price packages — like "Concrete Sidewalk" or "Brick Refacing" —
                    then snap them into any bid estimate in seconds.
                </Text>
                <Button leftIcon={<MdAdd />} colorScheme="brand" size="sm" w="full" onClick={onNew}>
                    Create First Price Set
                </Button>
            </Box>
        </Center>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PriceSets() {
    const { data: priceSets, isLoading } = usePriceSets();
    const { isOpen: isNewOpen, onOpen: onNewOpen, onClose: onNewClose } = useDisclosure();
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [showIntel, setShowIntel] = useState(true);

    const sets = useMemo(() => priceSets || [], [priceSets]);

    // Auto-select first set when list loads
    useEffect(() => {
        if (!selectedId && sets.length > 0) {
            setSelectedId(sets[0].id);
        }
    }, [sets, selectedId]);

    const handleCreated = (id: number) => {
        setSelectedId(id);
    };

    const headerBg = useColorModeValue('white', 'navy.800');
    const headerBorder = useColorModeValue('gray.200', 'navy.700');
    const headerTitleColor = useColorModeValue('gray.800', 'gray.100');
    const workspaceBg = useColorModeValue('gray.50', 'navy.900');

    return (
        <Box h="calc(100vh - 80px)" display="flex" flexDir="column">
            {/* Top bar */}
            <Flex
                justify="space-between"
                align="center"
                px={6}
                py={3}
                borderBottom="1px solid"
                borderColor={headerBorder}
                bg={headerBg}
                flexShrink={0}
            >
                <HStack spacing={3}>
                    <Icon as={MdAttachMoney} color="brand.500" boxSize={5} />
                    <Heading size="md" color={headerTitleColor}>Price Sets</Heading>
                    {sets.length > 0 && (
                        <Badge colorScheme="brand" variant="subtle">{sets.length}</Badge>
                    )}
                </HStack>
                <HStack spacing={2}>
                    <Tooltip label={showIntel ? 'Hide market intel' : 'Show market intel'} hasArrow>
                        <IconButton
                            aria-label="Toggle market intel"
                            icon={<MdTrendingUp />}
                            size="sm"
                            variant={showIntel ? 'solid' : 'ghost'}
                            colorScheme={showIntel ? 'brand' : 'gray'}
                            onClick={() => setShowIntel(!showIntel)}
                        />
                    </Tooltip>
                    <Button leftIcon={<MdAdd />} colorScheme="brand" size="sm" onClick={onNewOpen}>
                        New Price Set
                    </Button>
                </HStack>
            </Flex>

            {/* 3-panel grid */}
            <Grid
                templateColumns={showIntel ? '240px 1fr 280px' : '240px 1fr'}
                flex={1}
                overflow="hidden"
                transition="grid-template-columns 0.2s ease"
            >
                {/* LEFT: Set library */}
                <GridItem overflow="hidden">
                    <PriceSetSidebar
                        sets={sets}
                        isLoading={isLoading}
                        selectedId={selectedId}
                        onSelect={setSelectedId}
                        onNew={onNewOpen}
                    />
                </GridItem>

                {/* CENTER: Workspace */}
                <GridItem overflow="auto" bg={workspaceBg}>
                    {selectedId ? (
                        <SetWorkspace setId={selectedId} />
                    ) : (
                        <EmptyWorkspace onNew={onNewOpen} />
                    )}
                </GridItem>

                {/* RIGHT: Market Intel (collapsible) */}
                {showIntel && (
                    <GridItem overflow="hidden">
                        <MarketIntelPanel setId={selectedId} />
                    </GridItem>
                )}
            </Grid>

            {/* New Set Modal */}
            <NewSetModal isOpen={isNewOpen} onClose={onNewClose} onCreated={handleCreated} />
        </Box>
    );
}
